import { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import "./styles/AppStream.css";
import logo from "./assets/logo.png";
import "./css/scroll.css";
import { handleLogin, handleLogout } from "./helpers/oauth";
import { exchangeCodeForToken, getUserInfo } from './helpers/oauth';
import { useScroll } from "./hooks.js/useScroll";
import "./css/welcome.css";
import "./css/footer.css"
import "./css/help.css";


const SHORT_MESSAGES = ["hey", "hello", "hi", "hola", "hey there", "hi there"];
const VALIDATION_RESPONSE = {
  type: "bot",
  validation: true,
  content:
    "🌟 **Hello!**\nI'd be happy to help! Could you please elaborate on your question so I can provide the most helpful response?",
};

let domain;
console.log("You are on host: " + window.location.hostname);
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  domain = 'http://localhost:8080';
} else {
  domain = 'https://test.conversation.goengen.com';
}

export const DOMAIN = domain;

const OIDC_CONFIG = {

  oauth_client_id: 'b30a412c-f8f1-e6d1-1e4dabd5693a3d21f316c203651e46b2',
  oauth_client_secret: 'cd337e48-6363-665b-273ca0b2fd742230d6beb16b21d32c45',
  oauth_domain: 'OAMAzureOAuthOidcExample',
  oauth_login_url: 'https://test.auth.highmark.com/oauth2/rest/authz',
  oauth_token_url: 'https://lab.auth.highmark.com/oauth2/rest/token',
  oauth_callback_url: 'https://localhost:4200/oauth/callback',
  oauth_userinfo_endpoint: 'https://lab.auth.highmark.com/oauth2/rest/userinfo',
  app_login_url: 'https://localhost:4200',
  scope: 'resource.READ'


};

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [userAuthToken, setAuthToken] = useState(null);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showSettingsDialog, setSettingsDialog] = useState(false);
  const [verbosityLevel, setVerbosityLevel] = useState("moderate");
  const helpDialogRef = useRef(null);
  const aboutDialogRef = useRef(null);
  const [firstMessageSent, setFirstMessageSent] = useState(false);


  const closeHelpDialog = () => {
    if (helpDialogRef.current) {
      helpDialogRef.current.style.animation = "fadeOut 0.3s ease-out forwards";
      setTimeout(() => {
        setShowHelpDialog(false);
      }, 300);
    }
  };

  const closeAboutDialog = () => {
    if (aboutDialogRef.current) {
      aboutDialogRef.current.style.animation = "fadeOut 0.3s ease-out forwards";
      setTimeout(() => {
        setShowAboutDialog(false);
      }, 300);
    }
  };
  // Debounced scroll functions
  const advancedScrollToBottom = useCallback(
    debounce((behavior = "smooth") => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }, 300),
    []
  );

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (!userInfo) {
        showAlert("Please sign in to send messages.", "sign-in-alert");
        return;
      }
      event.preventDefault();
      handleSend();
    }
  };

  const { showScrollButton, handleScrollToBottomClick: handleScrollToBottomClickFromHook } =
  useScroll(chatMessagesRef);

const handleScrollToBottomClick = () => {
  // Immediate scroll
  messagesEndRef.current?.scrollIntoView({
    behavior: "auto",
    block: "nearest",
    inline: "start",
  });
  // Debounced smooth scroll
  advancedScrollToBottom();
  handleScrollToBottomClickFromHook();
};

  

  useEffect(() => {
    const savedSession = sessionStorage.getItem("chatSession");
    const savedUser = sessionStorage.getItem("userInfo");
    if (savedSession) setSession(savedSession);
    if (savedUser) setUserInfo(JSON.parse(savedUser));
    handleOidcCallback();
    setTimeout(() => setShowWelcome(false), 2000);
  }, []);

  useEffect(() => {
    // Immediate scroll for new messages
    messagesEndRef.current?.scrollIntoView({
      behavior: "auto",
      block: "nearest",
      inline: "start",
    });

    // Debounced smooth scroll for updates
    advancedScrollToBottom();
  }, [messages, advancedScrollToBottom]);

  const markdownToHTML = (text) => {
    return text
      .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/  \n/g, "<br>");
  };

  const handleOidcCallback = async () => {
    console.log("Handling OIDC callback...");
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (code && !(sessionStorage.getItem("code_handled") === code)) {
      sessionStorage.setItem("code_handled", code);
      // Set a timeout to remove 'code_handled' after 2 minutes (120000 milliseconds)
      setTimeout(() => {
        sessionStorage.removeItem("code_handled");
        console.log("'code_handled' removed from sessionStorage after 2 minutes.");
      }, 120000); // Changed from 60000 to 120000
      try {
        const tokenData = await exchangeCodeForToken(code);
        setAuthToken(tokenData);
        const userInfo = await getUserInfo(tokenData.access_token);
        setUserInfo(userInfo);

        // Fetch user settings after successful login
        try {
          const settingsResponse = await fetch(`${DOMAIN}/discover/v1alpha/data-discovery/user-settings`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData.access_token}`,
            },
          });

          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            if (settingsData.verbosity) {
              setVerbosityLevel(settingsData.verbosity);
            } else {
              setVerbosityLevel("moderate");
            }
          } else {
            const errorData = await settingsResponse.json();
            console.error("Error fetching user settings:", errorData);
            showAlert("Failed to fetch user settings.", "error-alert");
          }
        } catch (error) {
          console.error("Error fetching user settings:", error);
          showAlert("Failed to fetch user settings.", "error-alert");
        }

        params.delete("code");
        const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
        window.history.replaceState({}, document.title, newUrl);
      } catch (error) {
        console.error("OAuth error:", error);
        showAlert(error.message, "error-alert");
        sessionStorage.removeItem("code_handled");
      } finally {
        sessionStorage.removeItem("oidc_state");
        sessionStorage.removeItem("code_verifier");
      }
    }
  };

  const showAlert = (message) => {
    const alert = document.createElement("div");
    alert.className = "coming-soon-alert feedback-alert show";
    alert.textContent = message;
    document.body.appendChild(alert);
    setTimeout(() => alert.remove(), 2000);
  };

  const handleFeedback = (message, feedbackType) => {
    showAlert("Thank you for your feedback!");
    console.log("Feedback:", feedbackType, message);
  };


  const handleLogoutClick = () => {
    handleLogout();
    clearUserInfo();
  };

  const clearUserInfo = () => {
    setUserInfo(null);
    setAuthToken(null)
  };


  const handleSend = async () => {
    const userMessage = input.trim();
    if (!userMessage) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    let currentContent = "";

    if (!firstMessageSent) {
      setFirstMessageSent(true);
      setShowWelcome(false);
    }

    try {
      // Add user message immediately
      setMessages((prev) => [...prev, { type: "user", content: userMessage }]);
      setInput("");
      setIsLoading(true);

      // Simulated API call
      const response = await fetch(
        `${DOMAIN}/discover/v1alpha/data-discovery/prompt`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: userInfo
              ? `Bearer ${userAuthToken.access_token}`
              : "Bearer ",
          },
          body: JSON.stringify({
            query: userMessage,
            ...(session && { session }),
            verbosity: verbosityLevel,
          }),
          signal: controller.signal,
        }
      );

      const data = await response.json();

      // Add initial bot message
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          content: "",
          isStreaming: true,
          references: [],
          relatedQuestions: [],
        },
      ]);

      const fullResponse = data.answer.answerText;
      const baseSpeed = 10; // 40 characters per second
      let CHUNK_SIZE = Math.floor(
        Math.min(Math.max(fullResponse.length * 0.05, 5), 80)
      );

      let CHUNK_DELAY = Math.floor((100 / baseSpeed) * CHUNK_SIZE);

      // Adjust for very long responses
      if (fullResponse.length > 1000) {
        CHUNK_SIZE = Math.min(80, Math.floor(fullResponse.length * 0.03));
        CHUNK_DELAY = 50;
      }

      console.log(`Streaming ${CHUNK_SIZE} chars every ${CHUNK_DELAY}ms`);

      const streamChunk = async (index = 0) => {
        if (controller.signal.aborted) return;

        // Dynamic chunk sizing for final portion
        const remaining = fullResponse.length - index;
        let dynamicChunkSize = CHUNK_SIZE;
        if (remaining < 100) {
          dynamicChunkSize = Math.max(2, Math.floor(remaining * 0.3));
        }

        currentContent = fullResponse.substring(0, index + dynamicChunkSize);

        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          return [
            ...prev.slice(0, -1),
            { ...lastMessage, content: currentContent },
          ];
        });

        const newIndex = index + dynamicChunkSize;
        if (newIndex < fullResponse.length) {
          // Dynamic speed adjustment
          let delay = CHUNK_DELAY;
          if (remaining < 200) {
            delay = Math.max(20, delay * 0.7);
          }

          // Add natural typing variation
          delay *= 1 - 0.2 + Math.random() * 0.4;

          await new Promise((resolve) => setTimeout(resolve, delay));
          await streamChunk(newIndex);
        }
      };

      await streamChunk(0);

      // Finalize successful response
      if (!controller.signal.aborted) {
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              content: fullResponse,
              isStreaming: false,
              relatedQuestions: data.answer.relatedQuestions || [],
              references: data.answer.references || [],
            },
          ];
        });

        if (data.session?.name) {
          setSession(data.session.name);
          sessionStorage.setItem("chatSession", data.session.name);
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              content: `${currentContent}\n\n**Stopped by user**`,
              isStreaming: false,
            },
          ];
        });
      } else {
        console.error("API Error:", error);
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            content: "Sorry, there was an error processing your request.",
            references: [],
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };


  const clearSession = () => {
    clearSessionData();
    setMessages([]);
    setInput("");
    //  setFirstMessageSent(false);
    setShowWelcome(true);
    inputRef.current.placeholder = "Type your message...";
  };

  const clearSessionData = () => {
    sessionStorage.removeItem("chatSession");
    setSession(null);
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleHelpClick = () => {
    setShowHelpDialog(true);
  };

  const handleSettingsClick = () => {
    setSettingsDialog(true);
  };

  const handleAboutClick = () => {
    setShowAboutDialog(true);
  };

  const handleVerbosityChange = async (level) => {
    setVerbosityLevel(level);
    try {
      const response = await fetch(`${DOMAIN}/discover/v1alpha/data-discovery/user-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: userInfo
            ? `Bearer ${userAuthToken.access_token}`
            : "Bearer ",
        },
        body: JSON.stringify({
          verbosity: level,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error updating verbosity level:", errorData);
        showAlert("Failed to update verbosity level.", "error-alert");
      } else {
        console.log("Verbosity level updated successfully.");
      }
    } catch (error) {
      console.error("Error updating verbosity level:", error);
      showAlert("Failed to update verbosity level.", "error-alert");
    }
  };

  const closeSettingsDialog = () => {
    if (aboutDialogRef.current) {
      aboutDialogRef.current.style.animation = "fadeOut 0.3s ease-out forwards";
      setTimeout(() => {
        setSettingsDialog(false);
      }, 300);
    }
  };

  const welcomeMessageContent = userInfo
    ? `Welcome  ${userInfo.firstname} ${userInfo.lastname}!`
    : "Archi is an AI-powered knowledge assistant designed to help you find information and answer your questions quickly and efficiently. Please sign in to get started";


  return (
    <div className="app">
      <header>
        <div className="header-content">
          <div className="header-left">
            <img src={logo} alt="Logo" className="header-logo" />
          </div>
          <div className="header-center">
            <h2 className="header-title"> discover</h2>
          </div>
          <div className="header-right">{userInfo ? (
            <div className="user-info">
              <span>
                {userInfo.firstname} {userInfo.lastname}&nbsp;
              </span>
              <span>
                <svg className="gear-icon" viewBox="0 0 24 24" width="24" height="24" onClick={handleSettingsClick}>
                  <path
                    fill="currentColor"
                    d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.26-.2.3-.58.1-.85l-1.9-3.28c-.19-.33-.57-.46-.9-.3l-2.45 1c-.4-.3-.83-.58-1.3-.8l-.36-2.54c-.05-.28-.28-.48-.55-.48h-3.82c-.27 0-.5.2-.55.48l-.36 2.54c-.47.22-.91.5-1.3.8l-2.45-1c-.33-.15-.71-.02-.9.3l-1.9 3.28c-.2.27-.16.65.1.85l2.03 1.58c-.05.3-.09.61-.09.94s.02.63.06.94l-2.03 1.58c-.26.2-.3.58-.1.85l1.9 3.28c.19.33.57.46.9.3l2.45-1c.4.3.83.58 1.3.8l.36 2.54c.05.28.28.48.55.48h3.82c.27 0 .5-.2.55-.48l.36-2.54c.47-.22.91-.5 1.3-.8l2.45 1c.33.15.71.02.9-.3l1.9-3.28c.2-.27.16-.65-.1-.85l-2.04-1.58zM12 15.75c-2.07 0-3.75-1.68-3.75-3.75s1.68-3.75 3.75-3.75 3.75 1.68 3.75 3.75-1.68 3.75-3.75 3.75z"
                  />
                </svg>
              </span>
              <button className="sign-in-btn" onClick={handleLogoutClick}>
                Sign Out
              </button>
            </div>
          ) : (
            <button className="sign-in-btn" onClick={handleLogin}>
              Sign In
            </button>
          )}</div>
        </div>
      </header>
      <div className="chat-container">
        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.map((msg, i) => (
            <Message
              key={i}
              message={msg}
              markdownToHTML={markdownToHTML}
              handleFeedback={handleFeedback}
              setInput={setInput}
              userAuthToken={userAuthToken}
            />
          ))}
          {isLoading && <LoadingDots />}
          <div ref={messagesEndRef} className="messages-end-anchor" />
        </div>
        <div
          className={`welcome-message-stream ${showWelcome ? "show" : ""} ${userInfo ? "logged-in" : "guest"
            } ${firstMessageSent && userInfo ? "hidden" : ""}`}
        >
          {welcomeMessageContent}
        </div>
      </div>

      <div
        className={`new-chat-container ${messages.length > 0 ? "new-chat-visible" : ""
          }`}
      >
        {isLoading}
        <button className="new-chat-btn" onClick={clearSession}>
          ask new question
        </button>
      </div>

      {showHelpDialog && (
        <div className="help-dialog-overlay" ref={helpDialogRef}>
          <div className="help-dialog">
            <button className="close-help-btn" onClick={closeHelpDialog}>
              &times;
            </button>
            <h3>How to Use Archi</h3>
            <div className="help-content">
              <p>&nbsp;</p>
              <p>
                Welcome to Archi, your AI-powered knowledge assistant! Here are
                some tips to get started:
              </p>
              <ul>
                <li>
                  <strong>Ask Questions:</strong> Type your questions in the
                  message box below and hit 'Send'.
                </li>
                <li>
                  <strong>New Question:</strong> Click 'ask new question' to
                  start a fresh conversation.
                </li>
                <li>
                  <strong>Sign In/Out:</strong> Use the 'Sign In' or 'Sign Out'
                  button to manage your session.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {showSettingsDialog && (
        <div className="help-dialog-overlay" ref={aboutDialogRef}>
        <div className="help-dialog">
          <button className="close-help-btn" onClick={closeSettingsDialog}>
            &times;
          </button>
          <h3>Settings</h3>
          <div className="help-content">
            <p>Verbosity Level:</p>
            <p>
              Adjust the level of detail in Archi's responses. Choose from the following options:
            </p>
            <div className="verbosity-options">
              <button
                className={`verbosity-btn ${verbosityLevel === "minimum" ? "active" : ""}`}
                onClick={() => handleVerbosityChange("minimum")}
              >
                Minimum
              </button>
              <button
                className={`verbosity-btn ${verbosityLevel === "moderate" ? "active" : ""}`}
                onClick={() => handleVerbosityChange("moderate")}
              >
                Moderate
              </button>
              <button
                className={`verbosity-btn ${verbosityLevel === "verbose" ? "active" : ""}`}
                onClick={() => handleVerbosityChange("verbose")}
              >
                Verbose
              </button>
            </div>
            <div className="verbosity-description">
              <p>
                <strong>Minimum:</strong> Provides concise answers, focusing on the most essential information.
                <br />
                <em>Example:</em> "The policy covers dental care."
              </p>
              <p>
                <strong>Moderate:</strong> Offers a balanced response with some context and explanation.
                <br />
                <em>Example:</em> "The policy includes coverage for routine dental care, such as cleanings and check-ups."
              </p>
              <p>
                <strong>Verbose:</strong> Delivers detailed answers with comprehensive explanations and additional information.
                <br />
                <em>Example:</em> "The policy provides extensive dental care coverage, including routine cleanings, check-ups, fillings, and certain types of oral surgery. Please refer to section 3.2 of the policy document for a complete list of covered procedures."
              </p>
            </div>
          </div>
        </div>
      </div>
      )}

      {showAboutDialog && (
        <div className="help-dialog-overlay" ref={aboutDialogRef}>
          <div className="help-dialog">
            <button className="close-help-btn" onClick={closeAboutDialog}>
              &times;
            </button>
            <h3>About Archi</h3>
            <div className="help-content">
              <p>&nbsp;</p>
              <p>
                Archi is an AI-powered knowledge assistant designed to help you
                find information and answer your questions quickly and
                efficiently. We have lot of enterprise information that is
                fragmented across the systems. With Aarchi we would like to
                connect those information and make it easily discoverable.
              </p>
              <p>
                It's built by enGen team, to provide you with a seamless and
                intelligent conversational experience.
              </p>
              <p>&nbsp;</p>
              <p>
                This is a alpha version, and we are continuously working to
                improve Archi's capabilities and expand its knowledge base.
              </p>
            </div>
          </div>
        </div>
      )}

      {userInfo && (
        <div
          className={`input-container ${messages.length === 0 ? "initial" : ""}`}
        >
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              placeholder="Type your message..."
              className="message-input"
              rows="2"
              disabled={isLoading}
            />
            {isLoading ? (
              <button className="stop-button" onClick={handleStop}>
                Stop
                <span className="stop-icon"></span>
              </button>
            ) : (
              <button
                className="send-button"
                onClick={handleSend}
                disabled={isLoading}
              >
                Send
              </button>
            )}
          </div>
        </div>
      )}

      <div
        className={`scroll-to-bottom ${showScrollButton ? "visible" : ""}`}
        onClick={handleScrollToBottomClick}
        role="button"
        tabIndex="0"
        title="Jump to latest message"
      >
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path
            fill="currentColor"
            d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"
          />
        </svg>
      </div>

      <footer>
        <div className="footer-content">
          <div className="footer-links">
            <a href="#" className="footer-link" onClick={handleHelpClick}>
              Help
            </a>
            <a href="#" className="footer-link" onClick={handleAboutClick}>
              About Archi
            </a>
          </div>
          <p className="footer-copyright">
            © Copyright 2025 enGen All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const Message = ({ message, markdownToHTML, handleFeedback, setInput, userAuthToken }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showAlert("Copied!");
  };

  const showAlert = (message) => {
    const alert = document.createElement("div");
    alert.className = "coming-soon-alert show feedback-alert show";
    alert.textContent = message;
    document.body.appendChild(alert);
    setTimeout(() => alert.remove(), 1000);
  };

  return (
    <div
      className={`message ${message.type}-message ${message.validation ? "validation-message" : ""
        }`}
    >
      {message.validation ? (
        <div
          className="validation-text"
          dangerouslySetInnerHTML={{ __html: markdownToHTML(message.content) }}
        />
      ) : (
        <>
          {message.type === "bot" && message.isStreaming ? (
            <div className="streaming-message">{message.content}</div>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  message.type === "bot"
                    ? markdownToHTML(message.content)
                    : message.content,
              }}
            />
          )}

          {message.type === "bot" && !message.isStreaming && (
            <div className="action-container">
              <div className="feedback-buttons">
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard(message.content)}
                  title="Copy"
                  aria-label="Copy"
                >
                  <svg viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12V1z"
                    />
                  </svg>
                </button>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback(message, "positive")}
                  title="Thumbs Up"
                  aria-label="Thumbs Up"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12.1318 2.50389C12.3321 2.15338 12.7235 1.95768 13.124 2.00775L13.5778 2.06447C16.0449 2.37286 17.636 4.83353 16.9048 7.20993L16.354 8.99999H17.0722C19.7097 8.99999 21.6253 11.5079 20.9313 14.0525L19.5677 19.0525C19.0931 20.7927 17.5124 22 15.7086 22H6C4.34315 22 3 20.6568 3 19V12C3 10.3431 4.34315 8.99999 6 8.99999H8C8.25952 8.99999 8.49914 8.86094 8.6279 8.63561L12.1318 2.50389ZM10 20H15.7086C16.6105 20 17.4008 19.3964 17.6381 18.5262L19.0018 13.5262C19.3488 12.2539 18.391 11 17.0722 11H15C14.6827 11 14.3841 10.8494 14.1956 10.5941C14.0071 10.3388 13.9509 10.0092 14.0442 9.70591L14.9932 6.62175C15.3384 5.49984 14.6484 4.34036 13.5319 4.08468L10.3644 9.62789C10.0522 10.1742 9.56691 10.5859 9 10.8098V19C9 19.5523 9.44772 20 10 20ZM7 11V19C7 19.3506 7.06015 19.6872 7.17071 20H6C5.44772 20 5 19.5523 5 19V12C5 11.4477 5.44772 11 6 11H7Z"
                    />
                  </svg>
                </button>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback(message, "negative")}
                  title="Thumbs Down"
                  aria-label="Thumbs Down"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M11.8727 21.4961C11.6725 21.8466 11.2811 22.0423 10.8805 21.9922L10.4267 21.9355C7.95958 21.6271 6.36855 19.1665 7.09975 16.7901L7.65054 15H6.93226C4.29476 15 2.37923 12.4921 3.0732 9.94753L4.43684 4.94753C4.91145 3.20728 6.49209 2 8.29589 2H18.0045C19.6614 2 21.0045 3.34315 21.0045 5V12C21.0045 13.6569 19.6614 15 18.0045 15H16.0045C15.745 15 15.5054 15.1391 15.3766 15.3644L11.8727 21.4961ZM14.0045 4H8.29589C7.39399 4 6.60367 4.60364 6.36637 5.47376L5.00273 10.4738C4.65574 11.746 5.61351 13 6.93226 13H9.00451C9.32185 13 9.62036 13.1506 9.8089 13.4059C9.99743 13.6612 10.0536 13.9908 9.96028 14.2941L9.01131 17.3782C8.6661 18.5002 9.35608 19.6596 10.4726 19.9153L13.6401 14.3721C13.9523 13.8258 14.4376 13.4141 15.0045 13.1902V5C15.0045 4.44772 14.5568 4 14.0045 4ZM17.0045 13V5C17.0045 4.64937 16.9444 4.31278 16.8338 4H18.0045C18.5568 4 19.0045 4.44772 19.0045 5V12C19.0045 12.5523 18.5568 13 18.0045 13H17.0045Z"
                    />
                  </svg>
                </button>
              </div>

              {message.relatedQuestions?.length > 0 && (
                <div className="related-questions">
                  {message.relatedQuestions.map((question, i) => (
                    <button
                      key={i}
                      className="related-question-btn"
                      onClick={() => setInput(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}

              {message.references?.length > 0 && (
                <References references={message.references} userAuthToken={userAuthToken} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const References = ({ references, userAuthToken }) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);

  const filteredRefs = references.filter(
    (ref) => ref.chunkInfo.relevanceScore >= 0.5
  );

  /**
   * 
   * @param {*} uri 
   */
  const handleViewSourceDocument = async (uri) => {
    console.log("handleViewSourceDocument called" + uri);
    try {
      // Construct the JSON payload with correct key "doc-link" and the uri value
      const payload = { "doc-link": uri };

      const response = await fetch(`${DOMAIN}/discover/v1alpha/data-discovery/stream`, {
        method: 'POST',
        headers: {
          'accept': 'application/octet-stream',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userAuthToken.access_token}`, // Include the authentication token
        },
        // Send the payload using JSON.stringify
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error streaming document:', errorData);
        showAlert('Failed to stream document.');
        return;
      }
      showAlert('Stream document request sent.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      console.log('Stream document request sent.');
    } catch (error) {
      console.error('Error streaming document:', error);
      showAlert('Failed to stream document.');
    }
  };

  const showAlert = (message) => {
    const alert = document.createElement("div");
    alert.className = "coming-soon-alert show";
    alert.textContent = message;
    document.body.appendChild(alert);
    setTimeout(() => alert.remove(), 1000);
  };

  return (
    <div className="references-section">
      <div
        className="section-header"
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
      >
        References ({filteredRefs.length})
        <svg
          className={`section-chevron ${isSectionExpanded ? "expanded" : ""}`}
          viewBox="0 0 24 24"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
        </svg>
      </div>

      <div className={`section-content ${isSectionExpanded ? "expanded" : ""}`}>
        {filteredRefs.map((ref, i) => {
          const metadata = ref.chunkInfo.documentMetadata;
          const uniqueId = `${metadata.uri}-${ref.chunkInfo.relevanceScore}-${i}`;
          const isItemExpanded = expandedItemId === uniqueId;

          return (
            <div key={uniqueId} className="reference-item">
              <div
                className="reference-header"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedItemId(isItemExpanded ? null : uniqueId);
                }}
              >
                <div className="header-text">
                  <small>{metadata.title}&nbsp;</small>
                  <small>
                    Relevance: {Math.round(ref.chunkInfo.relevanceScore * 100)}%
                  </small>
                </div>
                <svg
                  className={`chevron-icon ${isItemExpanded ? "expanded" : ""}`}
                  viewBox="0 0 24 24"
                >
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                </svg>
              </div>
              <div
                className={`reference-content ${isItemExpanded ? "expanded" : ""
                  }`}
              >
                <div className="chunk-info">
                  {ref.chunkInfo.content.split(" ").slice(0, 40).join(" ")}...
                </div>
                <a
                  href="#" // change the href to # and pass the function
                  className="doc-link"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent default link behavior

                    handleViewSourceDocument(metadata.uri); // Call function to handle POST
                  }}
                >
                  View Source Document
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LoadingDots = () => (
  <div className="loading-dots">
    <span></span>
    <span></span>
    <span></span>
  </div>
);

export default App;